import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/auth.guard';
import { orgSettingsGuard, orgSettingsHomeGuard, permissionGuard } from './core/permission.guard';
import { Perm } from './core/permissions';

const comingSoon = () => import('./pages/workspace/coming-soon').then((m) => m.ComingSoonPage);

export const routes: Routes = [
  {
    path: '',
    canActivate: [guestGuard],
    loadComponent: () => import('./layout/auth-shell').then((m) => m.AuthShell),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'login' },
      { path: 'login', loadComponent: () => import('./pages/login/login').then((m) => m.LoginPage) },
      {
        path: 'forgot-password',
        loadComponent: () => import('./pages/forgot-password/forgot-password').then((m) => m.ForgotPasswordPage),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./pages/reset-password/reset-password').then((m) => m.ResetPasswordPage),
      },
    ],
  },
  {
    path: 'app',
    canActivate: [authGuard],
    children: [
      {
        path: 'org-settings',
        canActivate: [orgSettingsGuard],
        loadComponent: () =>
          import('./pages/org-settings/org-settings-shell').then((m) => m.OrgSettingsShell),
        children: [
          {
            path: '',
            pathMatch: 'full',
            canActivate: [orgSettingsHomeGuard],
            loadComponent: comingSoon,
          },
          {
            path: 'dashboard',
            canActivate: [permissionGuard],
            data: { permission: Perm.dashboardRead },
            loadComponent: () => import('./pages/org-settings/dashboard').then((m) => m.OrgDashboardPage),
          },
          {
            path: 'users',
            canActivate: [permissionGuard],
            data: { permission: Perm.usersRead },
            loadComponent: () => import('./pages/org-settings/users').then((m) => m.UsersPage),
          },
          {
            path: 'roles/new',
            canActivate: [permissionGuard],
            data: { permission: Perm.rolesCreate },
            loadComponent: () => import('./pages/org-settings/role-editor').then((m) => m.RoleEditorPage),
          },
          {
            path: 'roles/:id/edit',
            canActivate: [permissionGuard],
            data: { permission: Perm.rolesUpdate },
            loadComponent: () => import('./pages/org-settings/role-editor').then((m) => m.RoleEditorPage),
          },
          {
            path: 'roles/:id',
            canActivate: [permissionGuard],
            data: { permission: Perm.rolesRead },
            loadComponent: () => import('./pages/org-settings/role-editor').then((m) => m.RoleEditorPage),
          },
          {
            path: 'roles',
            canActivate: [permissionGuard],
            data: { permission: Perm.rolesRead },
            loadComponent: () => import('./pages/org-settings/roles').then((m) => m.RolesPage),
          },
          {
            path: 'workspaces',
            canActivate: [permissionGuard],
            data: { permission: Perm.workspacesRead },
            loadComponent: () => import('./pages/org-settings/workspaces').then((m) => m.WorkspacesPage),
          },
          {
            path: 'audit',
            canActivate: [permissionGuard],
            data: { permission: Perm.auditRead },
            loadComponent: () => import('./pages/org-settings/audit-trail').then((m) => m.AuditTrailPage),
          },
          {
            path: 'appearance',
            canActivate: [permissionGuard],
            data: { permission: Perm.appearanceRead },
            loadComponent: () => import('./pages/org-settings/appearance').then((m) => m.AppearancePage),
          },
          {
            path: 'notifications',
            canActivate: [permissionGuard],
            data: { permission: Perm.notificationsRead },
            loadComponent: () =>
              import('./pages/org-settings/org-notifications').then((m) => m.OrgNotificationsPage),
          },
          {
            path: 'plants',
            canActivate: [permissionGuard],
            data: { permission: Perm.orgStructureManage },
            loadComponent: () => import('./pages/org-settings/plants').then((m) => m.PlantsPage),
          },
          {
            path: 'departments',
            canActivate: [permissionGuard],
            data: { permission: Perm.orgStructureManage },
            loadComponent: () => import('./pages/org-settings/departments').then((m) => m.DepartmentsPage),
          },
          {
            path: 'designations',
            canActivate: [permissionGuard],
            data: { permission: Perm.orgStructureManage },
            loadComponent: () => import('./pages/org-settings/designations').then((m) => m.DesignationsPage),
          },
        ],
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/settings/settings-shell').then((m) => m.SettingsShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'profile' },
          {
            path: 'profile',
            loadComponent: () => import('./pages/settings/profile').then((m) => m.ProfilePage),
          },
          {
            path: 'password',
            loadComponent: () => import('./pages/security/security').then((m) => m.SecurityPage),
          },
          {
            path: 'notifications',
            loadComponent: () =>
              import('./pages/settings/user-notifications').then((m) => m.UserNotificationsPage),
          },
        ],
      },
      {
        path: '',
        loadComponent: () => import('./layout/app-shell').then((m) => m.AppShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'chat' },
          { path: 'home', redirectTo: 'chat' },
          {
            path: 'chat',
            loadComponent: () => import('./pages/workspace/new-chat').then((m) => m.NewChatPage),
          },
          {
            path: 'projects',
            loadComponent: () =>
              import('./pages/workspace/projects-list').then((m) => m.ProjectsListPage),
          },
          {
            path: 'projects/:id',
            loadComponent: () =>
              import('./pages/workspace/project-details').then((m) => m.ProjectDetailsPage),
          },
          {
            path: 'workspaces',
            loadComponent: () =>
              import('./pages/workspace/assigned-workspaces').then((m) => m.AssignedWorkspacesPage),
          },
          {
            path: 'recruiting',
            loadComponent: () =>
              import('./pages/recruiting/recruiting-shell').then((m) => m.RecruitingShell),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
              {
                path: 'job-descriptions',
                canActivate: [permissionGuard],
                data: { permission: Perm.recruitingJobDescriptionsView },
                loadComponent: () =>
                  import('./pages/recruiting/job-descriptions').then((m) => m.JobDescriptionsPage),
              },
              {
                path: 'dashboard',
                canActivate: [permissionGuard],
                data: { permission: Perm.recruitingPositionsView, title: 'Recruitment Dashboard' },
                loadComponent: () =>
                  import('./pages/recruiting/recruiting-dashboard').then((m) => m.RecruitingDashboardPage),
              },
              {
                path: 'openings',
                canActivate: [permissionGuard],
                data: { permission: Perm.recruitingPositionsView, title: 'Active Openings' },
                loadComponent: () =>
                  import('./pages/recruiting/active-openings').then((m) => m.ActiveOpeningsPage),
              },
              {
                path: 'candidates',
                canActivate: [permissionGuard],
                data: { permission: Perm.recruitingCandidatesView, title: 'Candidates' },
                loadComponent: () =>
                  import('./pages/recruiting/candidates').then((m) => m.CandidatesPage),
              },
            ],
          },
          {
            path: 'tasks',
            loadComponent: () => import('./pages/tasks/task-shell').then((m) => m.TaskShell),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'my' },
              {
                path: 'my',
                canActivate: [permissionGuard],
                data: { permission: Perm.taskAccess, title: 'My Tasks' },
                loadComponent: () => import('./pages/tasks/my-tasks').then((m) => m.MyTasksPage),
              },
              {
                path: 'team',
                canActivate: [permissionGuard],
                data: { permission: Perm.taskTeamView, title: 'Team Tasks' },
                loadComponent: () => import('./pages/tasks/team-tasks').then((m) => m.TeamTasksPage),
              },
            ],
          },
          {
            path: 'meeting',
            loadComponent: () =>
              import('./pages/meeting/meeting-shell').then((m) => m.MeetingShell),
            children: [
              { path: '', pathMatch: 'full', redirectTo: 'meetings' },
              {
                path: 'meetings',
                canActivate: [permissionGuard],
                data: { permission: Perm.meetingMeetingsView },
                loadComponent: () =>
                  import('./pages/meeting/meetings-home').then((m) => m.MeetingMeetingsPage),
              },
              {
                // A meeting opens in the side drawer on the calendar page. Old links to a meeting land there.
                path: 'meetings/:id',
                redirectTo: ({ params }) =>
                  inject(Router).createUrlTree(['/app/meeting/meetings'], {
                    queryParams: { meeting: params['id'], tab: 'overview' },
                  }),
              },
              { path: 'connections', redirectTo: 'meetings' },
            ],
          },
          {
            path: 'coming-soon',
            loadComponent: comingSoon,
          },
          { path: 'security', redirectTo: '/app/settings/password' },
          {
            path: '**',
            loadComponent: comingSoon,
            data: { title: 'This page' },
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
